<script lang="ts">
  import { board, filters, isPhone } from "../lib/stores";
  import type { SortKey } from "../lib/stores";
  import type { Vote } from "../lib/types";
  import { parseISODate } from "../lib/date";
  import { t, localeTag } from "../lib/i18n";

  const focusActive = $derived($filters.focusMembers.length > 0);

  /** Collapsed by default on phones: expanded, this panel was a screen and a
   * half of chips before any day was visible. */
  let open = $state(false);

  const short = (iso: string) =>
    parseISODate(iso).toLocaleDateString($localeTag, { day: "2-digit", month: "2-digit" });

  /** What the collapsed header says, so folding the panel away never hides
   * which filters are actually in force. */
  const summary = $derived.by(() => {
    const parts = [`${short($filters.rangeFrom)} → ${short($filters.rangeTo)}`];
    if (focusActive) {
      parts.push($t("controls.focusN", { n: $filters.focusMembers.length }));
    }
    if ($filters.view === "timeline" && $filters.sortBy !== "date") {
      parts.push($t(sortOptions.find((o) => o.key === $filters.sortBy)?.i18n ?? "sort.date"));
    }
    return parts.join(" · ");
  });

  const activeCount = $derived(
    $filters.focusMembers.length +
      ($filters.view === "timeline" && $filters.sortBy !== "date" ? 1 : 0),
  );

  function setRange(key: "rangeFrom" | "rangeTo", value: string) {
    if (value) filters.update((f) => ({ ...f, [key]: value }));
  }

  function toggleFocus(id: string) {
    filters.update((f) => {
      const has = f.focusMembers.includes(id);
      const focusMembers = has
        ? f.focusMembers.filter((x) => x !== id)
        : [...f.focusMembers, id];
      return { ...f, focusMembers };
    });
  }

  function clearFocus() {
    filters.update((f) => ({ ...f, focusMembers: [] }));
  }

  function setFocusVote(vote: Vote) {
    filters.update((f) => ({ ...f, focusVote: vote }));
  }

  function setSort(sortBy: SortKey) {
    filters.update((f) => ({ ...f, sortBy }));
  }

  const sortOptions: { key: SortKey; i18n: string }[] = [
    { key: "date", i18n: "sort.date" },
    { key: "yes", i18n: "sort.yes" },
    { key: "total", i18n: "sort.total" },
    { key: "maybe", i18n: "sort.maybe" },
    { key: "no", i18n: "sort.no" },
  ];
</script>

<!-- One definition, rendered either inline (wide) or inside the phone
     disclosure — so the two can never drift apart. -->
{#snippet groups()}
  <div class="group range">
    <span class="lab">{$t("controls.range")}</span>
    <input
      type="date"
      value={$filters.rangeFrom}
      onchange={(e) => setRange("rangeFrom", e.currentTarget.value)}
      aria-label={$t("controls.rangeStart")}
    />
    <span class="dash">{$isPhone ? "↓" : "→"}</span>
    <input
      type="date"
      value={$filters.rangeTo}
      onchange={(e) => setRange("rangeTo", e.currentTarget.value)}
      aria-label={$t("controls.rangeEnd")}
    />
  </div>

  <div class="group focus">
    <span class="lab">{$t("controls.onlyWhere")}</span>
    <div class="chips">
      {#if $board.members.length === 0}
        <span class="muted">{$t("controls.noMembers")}</span>
      {:else}
        {#each $board.members as m (m.id)}
          <button
            type="button"
            class="chip"
            class:on={$filters.focusMembers.includes(m.id)}
            aria-pressed={$filters.focusMembers.includes(m.id)}
            onclick={() => toggleFocus(m.id)}
          >
            {m.name}
          </button>
        {/each}
      {/if}
    </div>
    <span class="lab">{$t("controls.voted")}</span>
    <select
      value={$filters.focusVote}
      onchange={(e) => setFocusVote(e.currentTarget.value as Vote)}
      aria-label={$t("controls.requiredVote")}
    >
      <option value="yes">{$t("vote.yes")}</option>
      <option value="maybe">{$t("vote.maybe")}</option>
      <option value="no">{$t("vote.no")}</option>
    </select>
    <!-- always rendered (space reserved) so appearing/disappearing never
         reflows the row or shifts the neighbouring controls -->
    <button
      type="button"
      class="clear"
      class:hidden={!focusActive}
      onclick={clearFocus}
      tabindex={focusActive ? 0 : -1}
      aria-hidden={!focusActive}>{$t("controls.clear")}</button
    >
  </div>

  {#if $filters.view === "timeline"}
    <div class="group sort">
      <span class="lab">{$t("controls.sort")}</span>
      <div class="seg">
        {#each sortOptions as o (o.key)}
          <button
            type="button"
            class="seg-btn"
            class:on={$filters.sortBy === o.key}
            onclick={() => setSort(o.key)}
          >
            {$t(o.i18n)}
          </button>
        {/each}
        {#if focusActive}
          <button
            type="button"
            class="seg-btn accent"
            class:on={$filters.sortBy === "focus"}
            onclick={() => setSort("focus")}
          >
            {$t("sort.focus")}
          </button>
        {/if}
      </div>
    </div>
  {/if}
{/snippet}

{#if $isPhone}
  <section class="controls panel collapsible" class:open>
    <button class="disclosure" onclick={() => (open = !open)} aria-expanded={open}>
      <span class="d-head">
        <span class="d-title">{$t("controls.filters")}</span>
        {#if activeCount > 0}
          <span class="d-badge">{activeCount}</span>
        {/if}
      </span>
      <span class="d-summary">{summary}</span>
      <span class="chev" class:up={open} aria-hidden="true">⌄</span>
    </button>
    {#if open}
      <div class="body">
        {@render groups()}
      </div>
    {/if}
  </section>
{:else}
  <section class="controls panel">
    {@render groups()}
  </section>
{/if}

<style>
  .controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 18px;
    padding: 14px 16px;
  }
  .group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .lab {
    color: var(--muted);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .range input {
    min-height: 38px;
  }
  .dash {
    color: var(--muted);
    font-weight: 900;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    min-height: 32px;
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0 12px;
    background: var(--surface);
    color: var(--ink);
    font-size: 13px;
    font-weight: 700;
  }
  .chip.on {
    color: var(--on-yes);
    background: var(--yes);
    border-color: var(--yes);
  }
  .focus select {
    min-height: 36px;
  }
  .clear {
    border: 0;
    background: transparent;
    color: var(--no-ink);
    font-weight: 800;
    font-size: 13px;
  }
  .clear.hidden {
    visibility: hidden;
    pointer-events: none;
  }
  .seg {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px;
    border: 1px solid var(--line);
    border-radius: 12px;
    background: var(--surface-a);
  }
  .seg-btn {
    min-height: 30px;
    border: 0;
    border-radius: 9px;
    padding: 0 12px;
    background: transparent;
    color: var(--muted);
    font-size: 13px;
    font-weight: 800;
  }
  .seg-btn.on {
    color: var(--btn-fg);
    background: var(--btn);
  }
  .seg-btn.accent.on {
    background: var(--yes);
  }
  .muted {
    color: var(--muted);
  }

  /* ── phone disclosure ─────────────────────────────────────────────────────── */
  .collapsible {
    display: block;
    padding: 0;
  }
  .disclosure {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 52px;
    padding: 8px 14px;
    border: 0;
    border-radius: 24px;
    background: transparent;
    color: var(--ink);
    text-align: left;
  }
  .d-head {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .d-title {
    font-size: 13px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--muted);
  }
  .d-badge {
    display: inline-grid;
    place-items: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--yes);
    color: var(--on-yes);
    font-size: 12px;
    font-weight: 900;
  }
  .d-summary {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: right;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
  }
  .chev {
    font-size: 20px;
    font-weight: 900;
    line-height: 1;
    color: var(--muted);
    transition: transform 0.18s;
  }
  .chev.up {
    transform: rotate(180deg);
  }
  .collapsible .body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    padding: 4px 14px 16px;
    border-top: 1px solid var(--line);
    margin-top: 2px;
  }

  @media (max-width: 900px) {
    .controls {
      flex-direction: column;
      align-items: stretch;
      gap: 14px;
    }
    .group {
      justify-content: flex-start;
    }
    .range input {
      flex: 1;
      min-width: 0;
    }
    .seg {
      width: 100%;
    }
    .seg-btn {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media (max-width: 760px) {
    /* the segmented sort control cannot hold five German labels across one row
       ("Meiste Antworten" clipped to "Meiste A…"), so let it be a chip grid */
    .seg {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
    }
    .seg-btn {
      min-height: var(--tap);
      font-size: 13.5px;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      line-height: 1.2;
    }
    .chip {
      min-height: var(--tap);
      font-size: 14px;
      padding: 0 14px;
    }
    .lab {
      font-size: 12.5px;
    }
    .clear {
      min-height: var(--tap);
      font-size: 14px;
      padding: 0 4px;
    }
    /* Two native date pickers do not fit side by side at 320px, so the range
       stacks — and the separator becomes a downward arrow to match. */
    .range {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 6px;
      justify-items: stretch;
    }
    .range .dash {
      justify-self: center;
      font-size: 15px;
      line-height: 1;
    }
  }
</style>
